import type { Role } from '@/session.tsx';

/**
 * Why a control is disabled, in the words an operator needs — or `null` when it is not.
 *
 * Four screens disable something for a role that may not do it, and before this each of them
 * phrased the refusal itself. That is how "you do not have permission" gets written four
 * different ways, none of which names the role the reader actually holds or the thing they were
 * reaching for. One sentence, one place.
 *
 * `action` completes "which cannot …", so it is a verb phrase: `mint links`, not `minting`.
 */
export function roleRefusal(role: Role, action: string, allowed: boolean): string | null {
  return allowed ? null : `Your role is ${role}, which cannot ${action}.`;
}

/**
 * The first refusal that applies, as a `title` value.
 *
 * Order is the caller's, and it matters: a control is usually refused for more than one reason at
 * once — a viewer looking at a channel that points at nothing — and showing the role first is
 * right, because it is the one they cannot fix by changing the row.
 *
 * Returns `undefined` rather than `null` so it drops the attribute instead of rendering "null".
 */
export function refusal(...reasons: readonly (string | null | undefined)[]): string | undefined {
  return reasons.find((reason): reason is string => Boolean(reason));
}
